<template>
	<app-title-bar
		:icon="['fas', 'fa-clipboard-check']"
		subtitle="Hire Desk"
		title="Stock Check Detail"
		:badges="[contract?.contract_number, contract?.account_name]">
		<div class="flex gap-3">
			<app-button to="/hire-desk/stock-items" icon="fa-circle-left"> Back </app-button>

			<resource-details :loading="loading || !contract" :resource="contract" />

			<status-badge
				v-if="contract?.status && contract.status.length > 0"
				:status="contract.status[0]"
				status-key="stock_check_status" />
		</div>
	</app-title-bar>

	<div class="flex flex-col-reverse 2xl:flex-row gap-4">
		<app-panel class="grow" title="Item Details">
			<template #header>
				<div class="flex items-center gap-3">
					<contract-notes v-if="contract?.status[0].notes">
						{{ contract?.status[0].notes }}
					</contract-notes>

					<contract-notes
						v-if="contract?.status[0].item_notes"
						title="Ancillary Items"
						button="Ancillaries">
						{{ contract?.status[0].item_notes }}
					</contract-notes>

					<app-button
						v-if="contract"
						size="small"
						theme="danger"
						@click="confirmCancel = true">
						{{ cancelButtonText }}
					</app-button>

					<app-dropdown
						v-if="pickRefOptions && pickRefOptions.length > 0"
						v-model="pickRefs"
						size="small"
						:options="pickRefOptions"
						label-key="value"
						multiple
						@update:model-value="getData">
						Pick Ref
					</app-dropdown>
				</div>
			</template>

			<app-alert v-if="hasUnselectedChanges">
				You have unselected rows with changes
			</app-alert>

			<div :key="renderKey" class="flex flex-col">
				<item-details-table
					:items="items"
					:loading="loading"
					:pick-refs="pickRefOptions && pickRefOptions.length > 0"
					@clicked-item="(value: ContractItem) => (currentItem = value)"
					@selected-items="(value: ContractItem[]) => (selections = value)"
					@updated-items="getData"
					@changed-items="updated" />

				<send-to-yard
					:items="selections"
					class="mt-4"
					:changed="changed"
					@sent="getData"
					@save="save"
					@depot-selected="onDepotSelected" />

				<app-tabs class="mt-4" :nav="nav" theme="dark">
					<template #tab1>
						<contract-availability-table
							:items="contractAvailability"
							:loading="contractAvailabilityLoading" />
					</template>

					<template #tab2>
						<item-availability-table
							:contract-item="currentItem"
							@loading="nav[1].loading = true"
							@reloaded="enableTab" />
					</template>
				</app-tabs>
			</div>
		</app-panel>
	</div>

	<confirm-dialog :open="confirmCancel" @confirmed="cancel" @canceled="confirmCancel = false">
		Are you sure you want to skip this stock check?
	</confirm-dialog>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import ContractAvailabilityTable from '@/components/datatables/hire-desk/stock/ContractAvailabilityTable.vue'
	import ResourceDetails from '@/components/partials/panels/ResourceDetails.vue'
	import ContractService from '@/services/contract'
	import ItemAvailabilityTable from '@/components/datatables/all/ItemAvailabilityTable.vue'
	import ItemDetailsTable from '@/components/datatables/hire-desk/stock/ItemDetailsTable.vue'
	import SendToYard from '@/components/partials/cat2/SendToYard.vue'
	import StatusBadge from '@/components/partials/cat2/StatusBadge.vue'
	import { useRoute, useRouter } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import useContracts from '@/composables/contracts'
	import { Contract } from '@/data/schema/contract'
	import { ContractItem } from '@/data/schema/contract-item'
	import type { StatusUpdate } from '@/data/interfaces/status-update'
	import { getAllItems } from '@/utils/contract-items'

	// Resources
	const route = useRoute()
	const router = useRouter()
	const toaster = useToasterStore()
	const api = new ContractService()

	// Data
	const renderKey = ref(1)
	const contractNumber = ref()
	const contract = ref<Contract>()
	const contractAvailability = ref([])
	const currentItem = ref<ContractItem>()
	const items = ref<ContractItem[]>([])
	const loading = ref(true)
	const contractAvailabilityLoading = ref(true)
	const pickRefOptions = ref()
	const selections = ref<ContractItem[]>([])
	const pickRefs = ref([])
	const confirmCancel = ref(false)
	const selectedDepot = ref('')

	const cancelButtonText = computed(() => {
		return (
			'Skip ' +
			(selections.value.length > 0
				? `${selections.value.length} Stock Check${selections.value.length > 1 ? 's' : ''}`
				: 'Stock Check')
		)
	})

	const hasUnselectedChanges = computed(() => {
		const selectionIds = selections.value.map((selection) => selection.id)

		// Extended map to consider TOS changes to kit components
		const changedIds = items.value
			.flatMap((item) => [item, ...(item.kit_components || [])])
			.filter((kit) => kit.changed)
			.map((kit) => kit.id)

		return !changedIds.every((selection) => selectionIds.includes(selection))
	})

	// Tab navigation
	const nav = ref([
		{
			name: 'Contract Availability',
			id: '#contract-availability'
		},
		{
			name: 'Item Availability',
			id: '#item-availability',
			disabled: true,
			loading: false
		}
	])

	/**
	 * Get contract and contract item data
	 * @param init boolean - exclusive for onMounted
	 */
	async function getData(init = false) {
		loading.value = true
		selections.value = []

		const { fetchData, getPickingRefOptions } = useContracts()

		try {
			const data = await fetchData(contractNumber.value, {
				withContractStatus: true,
				withStockCheckStatus: true,
				pickRefs: pickRefs.value
			})
			contract.value = data.contract

			items.value = data.items.map((item) => {
				const newItem = {
					...item,
					stock_status: item.stock_check_status?.[0]?.status ?? null,
					changed: false
				}

				if (item.kit_components) {
					newItem.kit_components = item.kit_components.map((component) => ({
						...component,
						changed: false
					}))
				}

				return newItem
			})

			if (contract.value) {
				getContractAvailability(contract.value)

				if (init === true && items.value) {
					pickRefOptions.value = getPickingRefOptions(items.value)
					renderKey.value++
				}
			}

			loading.value = false
		} catch (error) {
			console.error(error)
			loading.value = false
		}
	}

	function updated(itemId: string, kitId: string) {
		const index = items.value.findIndex((i) => i.id === itemId)

		if (index !== -1 && !kitId) {
			items.value[index].changed = !items.value[index].changed
		}

		if (index !== -1 && kitId) {
			const kitIndex = items.value[index].kit_components!.findIndex((i) => i.id === kitId)
			items.value[index].kit_components![kitIndex].changed =
				!items.value[index]?.kit_components?.[kitIndex].changed
		}
	}

	/**
	 * Save function resource
	 * Gets updated items from items array by selection IDs
	 * @param item ContractItem
	 */
	function isItemSelected(item: ContractItem) {
		const selectionIds = selections.value.map((selection) => selection.id)

		if (selectionIds.includes(item.id)) {
			return true
		}

		return false
	}

	/**
	 * Save function resource
	 * Filters and maps TOS changes, returning TOS items and depot items
	 * @param items ContractItem[]
	 */
	function changedItems(items: ContractItem[]) {
		return {
			tosItems: items
				.filter((item) => item.changed)
				.map((item) => ({
					id: item.id,
					status_id: item.stock_check_status?.[0]?.id,
					tos: item.stock_check_status?.[0]?.tos
				})),
			depotItems: items
				.filter((item) => !item.changed)
				.map((item) => ({
					id: item.id,
					depot: selectedDepot.value
				}))
		}
	}

	function save() {
		if (selections.value.length > 0) {
			const selectedItems = getAllItems(items.value).filter(isItemSelected)
			const { tosItems, depotItems } = changedItems(selectedItems)

			toaster.working({
				text: 'Saving changes...',
				dismissable: false,
				timeout: 0
			})

			const saveTos = new Promise<string | null>((resolve, reject) => {
				if (tosItems.length > 0) {
					api.bulkUpdateStatus(tosItems, 'stock-status')
						.then(() => {
							resolve('TOS values saved successfully!')
						})
						.catch(() => {
							reject('Error occurred saving TOS values')
						})
				} else {
					resolve(null)
				}
			})

			const saveDepots = new Promise<string | null>((resolve, reject) => {
				if (depotItems.length > 0) {
					api.bulkUpdateDepots(depotItems)
						.then(() => {
							resolve('Depot values saved successfully!')
						})
						.catch(() => {
							reject('Error occurred saving depot values')
						})
				} else {
					resolve(null)
				}
			})

			// When all tasks complete
			Promise.all([saveTos, saveDepots])
				.then(([tosResult, depotResult]) => {
					toaster.clear()

					if (tosResult) {
						toaster.success({ text: tosResult })
					}

					if (depotResult) {
						toaster.success({ text: depotResult })
					}

					getData()
				})
				.catch((error) => {
					console.error(error)
					toaster.error({ text: error })
				})
		}
	}

	function onDepotSelected(depot: string) {
		selectedDepot.value = depot
	}

	const changed = computed(() => {
		return items.value.some((item) => item.changed)
	})

	/**
	 * Get data for contract availability table
	 * @param contract
	 */
	function getContractAvailability(contract: Contract) {
		if (contract?.items && contract?.items.length > 0) {
			api.getStockFulfilment(
				contract.delivery_postcode,
				contract.items.map(({ id, item_number, quantity_required }) => ({
					id,
					item_number,
					quantity_required
				}))
			)
				.then((res) => {
					contractAvailability.value = res.data.data
					contractAvailabilityLoading.value = false
				})
				.catch((error) => {
					console.error(error)
					contractAvailabilityLoading.value = false
					toaster.error({
						text: 'An error occurred retrieving contract availability'
					})
				})
		} else {
			contractAvailabilityLoading.value = false
		}
	}

	/**
	 * Cancel a contract, followed by all items
	 */
	async function cancel() {
		confirmCancel.value = false
		let cancellations: Array<StatusUpdate | ContractItem> = []

		// Cancel selections or all items
		if (selections.value.length > 0) {
			cancellations = selections.value
		} else {
			items.value.forEach((item) => {
				cancellations.push(item)

				if (item.type == 'Kit' && item.kit_components) {
					item.kit_components.forEach((kit) => {
						cancellations.push(kit)
					})
				}
			})
		}

		// Map status value of 4 to chosen items
		cancellations = cancellations.map((item) => ({
			id: item.id,
			status: 4
		}))

		// Loading state
		toaster.working({
			text: 'Skipping contract, please wait...',
			timeout: 0,
			dismissable: false
		})

		// Requests
		if (contract.value) {
			api.bulkCreateStatus(cancellations as StatusUpdate[], 'stock-status')
				.then(() => {
					selections.value = []
					toaster.clear()
					toaster.success({
						text: 'Selected stock checks skipped successfully'
					})

					if (cancellations.length == items.value.length) {
						router.push('/hire-desk')
					} else {
						getData()
					}
				})
				.catch((error) => {
					console.error(error)
					toaster.clear()
					toaster.error({
						text: 'Failed to skip some contract items',
						timeout: 0
					})
				})
		}
	}

	function enableTab() {
		nav.value[1].disabled = false
		nav.value[1].loading = false
	}

	onMounted(() => {
		contractNumber.value = route.params.id

		if (contractNumber.value) {
			getData(true)
		}
	})
</script>
